// Updated DeliverySelf component with partial address functionality

import {
  Box,
  Button,
  Flex,
  Text,
  useToast,
  VStack,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  useColorMode,
  Badge,
} from '@chakra-ui/react';
import { navigate, useLocation } from '@reach/router';
import { graphqlOperation, API, Auth } from 'aws-amplify';
import Location from 'aws-sdk/clients/location';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FaBuilding, FaUser, FaMapMarkerAlt, FaUserFriends } from 'react-icons/fa';
import { MdLocationOn } from 'react-icons/md';
import { debounce } from 'lodash';
import {
  updateCustomerContact,
  updateCustomerProfileDeliveryToId,
  createCustomerContactAddressOccasion,updateCustomerAddress // Add this mutation
} from '../../graphql/mutations';
import { searchAddress, getCustomerContacts,userAnalysis } from '../../graphql/queries';
import * as CartService from '../../services/cart-service';
import { graphql } from '../../utils/api';
import awsExports from '../../aws-exports';

const DeliverySelf = ({
  user,
  setIsDeliveryOpen,
  cart,
  setCartInvalidFlag,
  deliveryAddress,
  fetchAndSetUser,
  updateDeliveryAddress,
}) => {
  const toast = useToast();
  const [selectedDeliveryToId, setSelectedDeliveryToId] = useState(user?.deliveryToId);
  const [selectedDefaultAddress, setSelectedDefaultAddress] = useState();
  const [contactId, setContactId] = useState();
  const { userId } = user;
  const {colorMode} = useColorMode();
  const [allAddresses, setAllAddresses] = useState([]);
  const [cartQty, setCartQty] = useState(0);
  const [updatedAddress, setUpdatedAddress] = useState();
  const [selfContactDetails, setSelfContactDetails] = useState(null); // Store self contact details
  const location = useLocation();
  const [pendingNewAddress, setPendingNewAddress] = useState(false);

  // Search states
  const [searchResults, setSearchResults] = useState([]);
  const [inputText, setInputText] = useState('');
  const [client, setClient] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
useEffect(() => {
  if (autoSave && updatedAddress && selectedDeliveryToId) {
    handleSave();       // ✅ EXACT same logic as Save button
    setAutoSave(false); // reset
  }
}, [autoSave, updatedAddress, selectedDeliveryToId]);


  // Icon mapping for different address types
  const getAddressIcon = (addressType) => {
    switch (addressType?.toLowerCase()) {
      case 'work':
        return FaBuilding;
      case 'home':
        return FaUser;
      case 'custom':
      case 'other':
        return FaMapMarkerAlt;
      case 'friends':
        return FaUserFriends;
      default:
        return FaMapMarkerAlt;
    }
  };
  

  // Initialize AWS Location client
  useEffect(() => {
    const createClient = async () => {
      const credentials = await Auth.currentCredentials();
      const tempClient = new Location({
        credentials,
        region: awsExports.aws_project_region,
      });
      setClient(tempClient);
    };
    createClient();
  }, []);
  useEffect(() => {
  if (pendingNewAddress && allAddresses.length > 0) {
    const recentAddr = allAddresses.find((address) => address.addressType === "Recent");
    
    if (recentAddr) {
      setSelectedDeliveryToId(recentAddr.id);
      setUpdatedAddress(recentAddr);
      setSelectedDefaultAddress(recentAddr.id);
      setAutoSave(true);
      setPendingNewAddress(false); // Reset flag
    }
  }
}, [allAddresses, pendingNewAddress]);

  // Search address function
  const searchAddressFunc = async (str) => {
    try {
      const data = await API.graphql({
        query: searchAddress,
        variables: {
          input: {
            searchStr: str,
          },
        },
        authMode: 'API_KEY',
        authToken: awsExports.aws_appsync_apiKey,
      });
      setSearchResults(data?.data?.searchAddress?.items || []);
    } catch (err) {
      console.log(err);
      setSearchResults([]);
    }
  };

  // Search place using AWS Location
  const searchPlace = async (place) => {
    const params = {
      IndexName: awsExports.aws_geo_mapIndexName,
      MaxResults: 10,
      Text: place,
    };

    try {
      const addrData = await client.searchPlaceIndexForText(params).promise();
      return addrData;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  // Debounced search
  const debounceSearch = debounce(
    (searchText) => {
      if (searchText.trim()) {
        searchAddressFunc(searchText);
      } else {
        setSearchResults([]);
      }
    },
    300,
  );

  // Handle input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputText(value);
    setIsSearching(true);
    debounceSearch(value);
  };

  // Create partial address using self contact details
  const createPartialAddress = async (addressData) => {
    try {
      if (!selfContactDetails) {
        toast({
          title: 'Error',
          description: 'Self contact details not found',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return null;
      }

      // Prepare contact input using self contact details
      const contactInput = {
        userId: user.userId,
        contactCategory: 'Recent', 
        firstName: selfContactDetails.firstName || '',
        middleName: selfContactDetails.middleName || '',
        lastName: selfContactDetails.lastName || '',
        phoneNumber: selfContactDetails.phoneNumber || '',
        email: selfContactDetails.email || '',
      };
      console.log("CONTACTinPUT",contactInput)

      // Prepare address input with the new searched address
      const addressInput = [
        {
          addrLine1: addressData.addrLine1,
          addrLine2: addressData.addrLine2 || '',
          city: addressData.city,
          addrState: addressData.addrState,
          country: addressData.country || 'USA',
          postCode: addressData.postCode,
          latitude: addressData.latitude,
          longitude: addressData.longitude,
          addressType: 'Recent',
        },
      ];

      // Prepare customer input (no occasions for partial addresses)
      const customerInput = {
        CustomerContactInput: contactInput,
        CustomerOccasionInput: [], // Empty occasions array
        CustomerAddressInput: addressInput,
      };

      // Call the mutation
      const response = await graphql(
        graphqlOperation(createCustomerContactAddressOccasion, {
          input: customerInput,
        }),
      );

      console.log('Partial address created:', response);

      // Return the created contact ID and address ID
      return response?.data?.createCustomerContactAddressOccasion;
    } catch (error) {
      console.error('Error creating partial address:', error);
      toast({
        title: 'Error',
        description: 'Failed to save address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return null;
    }
  };
    const updatePartialAddress = async (updateInput) => {
      console.log("adressdata",updateInput)
    try {
      if (!selfContactDetails) {
        toast({
          title: 'Error',
          description: 'Self contact details not found',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return null;
      }

     
      const response = await graphql(
        graphqlOperation(updateCustomerAddress, {
          input: updateInput,
        }),
      );

      console.log('Partial address created:', response);

      // Return the created contact ID and address ID
      return response;
    } catch (error) {
      console.error('Error creating partial address:', error);
      toast({
        title: 'Error',
        description: 'Failed to save address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return null;
    }
  };

  // Handle selecting a search result
  const handleSearchResultClick = async (result) => {
    console.log("resultafter",result)
     
   
    const fullAddress = `${result.street_line}, ${result.city}, ${result.state}, ${result.zipcode}`;
    setInputText(fullAddress);
    setSearchResults([]);
    setIsSearching(false);

    // Get coordinates for the selected address
    if (client) {
      try {
        const data = await searchPlace(fullAddress);
        if (data && data.Results && data.Results.length > 0) {
          const lat = data.Results[0].Place.Geometry.Point[1];
          const lon = data.Results[0].Place.Geometry.Point[0];

          console.log("adress121212",allAddresses)
    const recentAddress=allAddresses.filter((address)=>
       address.addressType==="Recent"
    )
    console.log("recentadress",recentAddress)
    if(recentAddress.length===0){
      console.log("iam innn")
       const newAddressData = {
            addrLine1: result.street_line,
            city: result.city,
            addrState: result.state,
            postCode: result.zipcode,
            latitude: lat,
            longitude: lon,
          };
     const createdData = await createPartialAddress(newAddressData);
     

          if (createdData) {
            // Update delivery address in Redux store
            await updateDeliveryAddress({
              address: newAddressData,
              lat,
              lon,
              deliveryBasedOnIP: false,
              // localAddress: true,
            });

            toast({
              title: 'Success',
              description: 'Partial address saved. Complete details at checkout.',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });

            // Clear search
            setInputText('');
setPendingNewAddress(true);
            // Refresh the address list to show the newly created partial address
            await getNewContactDetails();
            

            // Fetch updated user data
            await fetchAndSetUser();

            // Handle cart invalidation and navigation
            if (cartQty > 0) {
              const invalidFlag = await CartService.setInvalidCartFlag(cart);
              setCartInvalidFlag(invalidFlag);
              if (invalidFlag) {
                navigate(`/availabilitysearch/${cart.id}`);
              }
            }

            // Close modal
            setIsDeliveryOpen(false);

            // Clear localStorage
            localStorage.removeItem('localFilters');
            localStorage.removeItem('store1');

            // Reload page if on specific routes
            if (
              location.pathname.includes('/category/') ||
              location.pathname.includes('/discountAmount/') ||
              location.pathname.includes('/discountPercent/') ||
              location.pathname.includes('/store/') ||
              location.pathname.includes('/Brand/') ||
              location.pathname.includes('/storeLocator/') ||
              location.pathname.includes('/country/') ||
              location.pathname.includes('/search/')
            ) {
              setTimeout(() => {
                window.location.reload(true);
              }, 100);
            }
          }
      
    }else{
        const updateInput = {
          id:recentAddress[0].id,
          addressType:recentAddress[0].addressType,
          firstName:recentAddress[0].firstName,
          lastName:recentAddress[0].lastName,
          middleName:recentAddress[0].middleName,
          phoneNumber:recentAddress[0].phoneNumber,
          customerContactId:recentAddress[0].customerContactId,

            addrLine1: result.street_line,
            city: result.city,
            addrState: result.state,
            postCode: result.zipcode,
            latitude: lat,
            longitude: lon,
          };
          console.log("inputofupdate",updateInput)
           const res = await updatePartialAddress(updateInput);
             if (res) {
            // Update delivery address in Redux store
            await updateDeliveryAddress({
              address: {
                  addrLine1: result.street_line,
            city: result.city,
            addrState: result.state,
            postCode: result.zipcode,
            latitude: lat,
            longitude: lon,

              },
              lat,
              lon,
              deliveryBasedOnIP: false,
              addressType: 'Recent',
              // localAddress: true,
            });

            toast({
              title: 'Success',
              description: 'Partial address saved. Complete details at checkout.',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });

            // Clear search
            setInputText('');

            // Refresh the address list to show the newly created partial address
            await getNewContactDetails();

            // Fetch updated user data
            await fetchAndSetUser();

            // Handle cart invalidation and navigation
            if (cartQty > 0) {
              const invalidFlag = await CartService.setInvalidCartFlag(cart);
              setCartInvalidFlag(invalidFlag);
              if (invalidFlag) {
                navigate(`/availabilitysearch/${cart.id}`);
              }
            }

            // Close modal
            //setIsDeliveryOpen(false);
            setSelectedDeliveryToId(recentAddress[0].id);
            setUpdatedAddress(updateInput);
            setSelectedDefaultAddress(recentAddress[0].id);
            setAutoSave(true);

            // Clear localStorage
            localStorage.removeItem('localFilters');
            localStorage.removeItem('store1');

            // Reload page if on specific routes
            if (
              location.pathname.includes('/category/') ||
              location.pathname.includes('/discountAmount/') ||
              location.pathname.includes('/discountPercent/') ||
              location.pathname.includes('/store/') ||
              location.pathname.includes('/Brand/') ||
              location.pathname.includes('/storeLocator/') ||
              location.pathname.includes('/country/') ||
              location.pathname.includes('/search/')
            ) {
              setTimeout(() => {
                window.location.reload(true);
              }, 100);
            }
          }
        




          
        }

        getUserAnalysis(fullAddress)

        }
      } catch (error) {
        console.error('Error updating address:', error);
        toast({
          title: 'Error',
          description: 'Failed to update delivery address',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      
      }
    }
  };

const getUserAnalysis = async (address)=>{
  console.log('intouseraddress',address)
    const graphqlQuery = graphqlOperation(userAnalysis, {
    page: "address",
    addressDetails: address
  });
  const res = await API.graphql({
    ...graphqlQuery,
    authMode: 'API_KEY',
    authToken: awsExports.aws_appsync_apiKey,
  });
  }

  const markAddressDefault = async () => {
    try {
      await graphql(
        graphqlOperation(updateCustomerContact, {
          input: { defaultAddressId: selectedDefaultAddress, id: contactId },
        }),
      );

      toast({
        title: 'Success',
        description: 'Successfully updated default Address',
        status: 'success',
        isClosable: true,
        duration: 1000,
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Something went wrong to update default Address',
        status: 'error',
        isClosable: true,
        duration: 5000,
      });
    }
  };

  const handleAddressFlags = async () => {
    const userInput = {
      userId: user.userId,
      deliveryToId: selectedDeliveryToId,
    };
    try {
      await graphql(
        graphqlOperation(updateCustomerProfileDeliveryToId, {
          input: userInput,
        }),
      );

      toast({
        title: 'Delivery Contact details updated',
        status: 'success',
      });

      await fetchAndSetUser();
      markAddressDefault(selectedDefaultAddress);

      if (cartQty > 0) {
        const result = await CartService.setInvalidCartFlag(cart);
        setCartInvalidFlag(result);
        if (result) {
          navigate(`/availabilitysearch/${cart.id}`);
        }
      }

      setIsDeliveryOpen(false);

      if (
        location.pathname.includes('/category/') ||
        location.pathname.includes('/discountAmount/') ||
        location.pathname.includes('/discountPercent/') ||
        location.pathname.includes('/store/') ||
        location.pathname.includes('/Brand/') ||
        location.pathname.includes('/storeLocator/') ||
        location.pathname.includes('/country/') ||
        location.pathname.includes('/search/')
      ) {
        setTimeout(() => {
          window.location.reload(true);
        }, 100);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddress = async () => {
    const userInput = {
      userId: user.userId,
      deliveryToId: selectedDeliveryToId,
    };
    try {
      await graphql(
        graphqlOperation(updateCustomerProfileDeliveryToId, {
          input: userInput,
        }),
      );

      toast({
        title: 'Delivery Contact details updated',
        status: 'success',
      });

      await fetchAndSetUser();
      markAddressDefault(selectedDefaultAddress);

      if (cartQty > 0) {
        cart.cartShipment.items.forEach(async (eachCartShipment) => {
          eachCartShipment.lineItems.forEach((product) => {
            product.itemInvalid = false;
          });
          await CartService.updateCartShipment(eachCartShipment);
        });
        const result = CartService.getInvalidCartFlag(cart);
        setCartInvalidFlag(result);
        if (result) {
          navigate(`/cart/${cart.id}`);
        }
      }

      setIsDeliveryOpen(false);

      if (
        location.pathname.includes('/category/') ||
        location.pathname.includes('/discountAmount/') ||
        location.pathname.includes('/discountPercent/') ||
        location.pathname.includes('/store/') ||
        location.pathname.includes('/Brand/') ||
        location.pathname.includes('/storeLocator/') ||
        location.pathname.includes('/country/') ||
        location.pathname.includes('/search/')
      ) {
        setTimeout(() => {
          window.location.reload(true);
        }, 100);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // Function to refresh addresses after adding new one
  const getNewContactDetails = () => {
    if (userId) {
      graphql(
        graphqlOperation(getCustomerContacts, {
          userId,
        }),
      ).then((response) => {
        const contactsList = response.data.CustomerContactsByCustomerProfileId.items;

        // Find and store Self contact details for partial address creation
        const selfContact = contactsList.find((c) => c.contactCategory === 'Self');
        if (selfContact) {
          setSelfContactDetails({
            firstName: selfContact.firstName,
            middleName: selfContact.middleName,
            lastName: selfContact.lastName,
            phoneNumber: selfContact.phoneNumber,
            email: selfContact.email,
          });
        }

        let addresses = [];
        contactsList.forEach((contact) => {
          const contactAddresses = contact.deliveryAddress.items.map((addr) => ({
            ...addr,
            contactCategory: contact.contactCategory,
            customerContactId:contact.id,
            contactName: contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
            isPartialAddress: contact.isPartialAddress || false, // Include partial flag
          }));
          addresses = [...addresses, ...contactAddresses];
        });
console.log("adresss1212",addresses)
        const defaultContact = contactsList.find((c) => c.defaultAddressId);
        if (defaultContact) {
          setContactId(defaultContact.id);
          setSelectedDefaultAddress(defaultContact.defaultAddressId);
        } else if (contactsList.length > 0) {
          setContactId(contactsList[0].id);
          setSelectedDefaultAddress(contactsList[0].defaultAddressId);
        }

        setAllAddresses(addresses);
      });
    }
  };

  useEffect(() => {
    getNewContactDetails();
  }, [userId]);

  useEffect(() => {
    if (cart !== null && cart !== undefined) {
      let qty = 0;
      cart.cartShipment.items.forEach((eachCartShipment) => {
        eachCartShipment.lineItems.forEach(() => {
          qty++;
        });
      });
      setCartQty(qty);
    } else {
      setCartQty(0);
    }
  }, [cart]);

  const handleSave = (addressToSave) => {
  const addr = addressToSave || updatedAddress;
  if (!addr) return;

  if (
    cart?.cartShipment?.items[0]?.deliveryAddress?.latitude !== addr?.latitude &&
    cart?.cartShipment?.items[0]?.deliveryAddress?.longitude !== addr?.longitude
  ) {
    handleAddressFlags();
  } else {
    handleAddress();
  }

  updateDeliveryAddress({
    address: {
      addrLine1: addr.addrLine1,
      city: addr.city,
      addrState: addr.addrState,
      postCode: addr.postCode,
      latitude: addr.latitude,
      longitude: addr.longitude,
    },
    lat: addr.latitude,
    lon: addr.longitude,
    deliveryBasedOnIP: false,
    isPartialAddress: addr.isPartialAddress,
    addressType: addr.addressType,
  });
  if(!cart){
    console.log('location@2',location.pathname.includes('checkout'))
    if(location.pathname.includes('checkout')){
      navigate('/');
    }
  }

  localStorage.removeItem('localFilters');
  localStorage.removeItem('store1');
};


  return (
    <>
      <Flex flexDirection="column" bg="white" maxH="600px" h="600px">
        {/* Search Bar Section - Fixed at top */}
        <Box p="20px" pb="16px" borderBottom="1px solid" borderColor="gray.200">
          <Text fontSize="lg" fontWeight="bold" mb="3">
            Search for New Address
          </Text>
          <VStack position="relative" w="100%" spacing="2">
            <InputGroup>
              <InputLeftElement pointerEvents="none" children={<MdLocationOn color="#B72618" />} />
              <Input
                fontFamily="Inter"
                placeholder="Enter full address"
                color={colorMode === 'light' ? 'black' : 'black'}
                _placeholder={{ color: 'gray.400' }}
                value={inputText}
                onChange={handleSearchChange}
                borderColor="gray.300"
                _focus={{ borderColor: 'brand.red', boxShadow: '0 0 0 1px #B72618' }}
              />
            </InputGroup>
            <Text fontSize="xs" color="gray.500" alignSelf="flex-start">
              Addresses on routes - type US Highway / route / state route - for accurate search
            </Text>

            {/* Search Results Dropdown */}
            {searchResults && searchResults.length > 0 && (
              <Box
                position="absolute"
                top="44px"
                left="0"
                right="0"
                bg="white"
                border="1px solid"
                borderColor="gray.300"
                borderRadius="md"
                boxShadow="lg"
                maxH="200px"
                overflowY="auto"
                zIndex="1000"
                css={{
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '10px',
                  },
                }}
              >
                <List spacing={0}>
                  {searchResults.map((result, idx) => (
                    <ListItem
                      key={idx}
                      px="4"
                      py="3"
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                      onClick={() => handleSearchResultClick(result)}
                      borderBottom={idx < searchResults.length - 1 ? '1px solid' : 'none'}
                      borderColor="gray.200"
                      fontFamily="Inter"
                      fontSize="sm"
                    >
                      <HStack>
                        <Icon as={MdLocationOn} color="brand.red" />
                        <Text color={colorMode === 'light' ? 'black' : 'black'}>
                          {`${result.street_line}, ${result.city}, ${result.state}, ${result.zipcode}`}
                        </Text>
                      </HStack>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </VStack>
        </Box>

        {/* Saved Addresses Section - Scrollable */}
        <Box px="20px" pt="16px" pb="4">
          <Text fontSize="lg" fontWeight="bold" mb="4">
            Saved Addresses
          </Text>
        </Box>

        <Box
          flex="1"
          overflowY="auto"
          px="20px"
          css={{
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}
        >
          <VStack spacing="3" align="stretch" pb="4">
            {allAddresses?.length > 0 ? (
               [...allAddresses]
    .sort((a, b) => {
      if (a.id === selectedDeliveryToId) return -1;
      if (b.id === selectedDeliveryToId) return 1;
      return 0;
    })
    .map((address, index) => {
                const {
                  id,
                  firstName,
                  addressType,
                  middleName,
                  lastName,
                  addrLine1,
                  addrLine2,
                  city,
                  state,
                  country,
                  postCode,
                  contactCategory,
                  contactName,
                  isPartialAddress,
                } = address;

                const isSelected = selectedDeliveryToId === id;
                const AddressIcon = getAddressIcon(addressType);

                return (
                  <Box
                    key={id}
                    bg={isSelected ? 'brand.red' : 'white'}
                    color={isSelected ? 'white' : 'black'}
                    border={isSelected ? 'none' : '1px solid #E0E0E0'}
                    borderRadius="lg"
                    p="4"
                    cursor="pointer"
                    onClick={() => {
                      setSelectedDeliveryToId(id);
                      setUpdatedAddress(address);
                      setSelectedDefaultAddress(id);

                      setAutoSave(true); // 🔥 trigger save AFTER state update
                    }}


                    transition="all 0.2s"
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: 'md',
                    }}
                  >
                    <HStack justify="space-between" align="start">
                      <HStack spacing="3" align="start" flex="1">
                        <Icon as={AddressIcon} boxSize="6" color={isSelected ? 'white' : 'gray.600'} />
                        <VStack align="start" spacing="1" flex="1">
                          <HStack>
                            <Text fontWeight="bold" fontSize="md">
                              {addressType || 'Address'}
                            </Text>
                            {isSelected && (
                              <Badge colorScheme="whiteAlpha" bg="whiteAlpha.300">
                                Selected
                              </Badge>
                            )}
                            {isPartialAddress && (
                              <Badge colorScheme={isSelected ? 'whiteAlpha' : 'orange'} bg={isSelected ? 'whiteAlpha.300' : 'orange.100'}>
                                Partial
                              </Badge>
                            )}
                          </HStack>
                          <Text fontSize="sm" opacity={isSelected ? 0.9 : 0.7}>
                            {contactCategory === 'Self'
                              ? `${firstName ? ` ${firstName}` : ''}`
                              : contactName || contactCategory}
                            {addrLine1 && ` ${addrLine1}`}
                          </Text>
                          <Text fontSize="sm" opacity={isSelected ? 0.9 : 0.7}>
                            {[addrLine2, city, state, country, postCode].filter(Boolean).join(', ')}
                          </Text>
                          {isPartialAddress && (
                            <Text fontSize="xs" opacity={isSelected ? 0.8 : 0.6} fontStyle="italic">
                              Complete details required at checkout
                            </Text>
                          )}
                        </VStack>
                      </HStack>
                      <Text>{contactCategory}</Text>
                    </HStack>
                  </Box>
                );
              })
            ) : (
              <Text color="gray.500" textAlign="center" py="10">
                No saved addresses found
              </Text>
            )}
          </VStack>
        </Box>

        {/* Action Buttons - Fixed at bottom */}
        {/* <HStack justify="flex-end" p="20px" pt="16px" borderTop="1px solid" borderColor="gray.200" bg="white">
          <Button
            variant="outline"
            colorScheme="gray"
            onClick={() => {
              setIsDeliveryOpen(false);
            }}
            px="8"
          >
            Cancel
          </Button>
          <Button bg="brand.red" color="white" _hover={{ bg: 'red.600' }} onClick={handleSave} px="8">
            Save
          </Button>
        </HStack> */}
      </Flex>
    </>
  );
};

const stateMapper = (state) => ({
  user: state.auth?.user,
  deliveryAddress: state.deliveryAddress?.deliveryAddress,
  cart: state.cart.cart,
  cartInvalidFalg: state.cart.cartInvalidFlag,
});

const dispatchMapper = (dispatch) => ({
  updateCart: dispatch.cart.updateCart,
  setCartInvalidFlag: dispatch.cart.setCartInvalidFlag,
  fetchAndSetUser: dispatch.auth.fetchAndSetUser,
  updateDeliveryAddress: dispatch.deliveryAddress.updateDeliveryAddress,
});

export default connect(stateMapper, dispatchMapper)(DeliverySelf);